import { useMemo, useCallback } from 'react';
import { format, parseISO, startOfISOWeek } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { usePanelState } from '@/app/navigation/usePanelState';
import type { MyWorkordersView } from '@/features/user-preferences/user-preferences.hooks';
import { WorkorderCard } from '../shared';
import { UserSwitcher } from '../myday/components/UserSwitcher';
import { WorkorderNavigation } from './components/WorkorderNavigation';
import { ViewModeToggle } from './components/ViewModeToggle';
import { DayGroupHeader } from './components/DayGroup';
import { groupByDay } from './components/dayGroups';
import { useMyWeekWorkorders } from './myweek.hooks';

interface MyWeekPanelProps extends RoutedPanelProps {
  mode: MyWorkordersView;
  onModeChange: (mode: MyWorkordersView) => void;
}

export const MyWeekPanel = ({
  params,
  path,
  mode,
  onModeChange,
  ...panelProps
}: MyWeekPanelProps) => {
  const dateParam = params.date || format(new Date(), 'yyyy-MM-dd');
  const weekStart = useMemo(
    () => startOfISOWeek(parseISO(dateParam)),
    [dateParam],
  );
  const weekStartIso = format(weekStart, 'yyyy-MM-dd');

  const [panelState, setPanelState] = usePanelState<{ userId?: string }>();
  const userId = panelState.userId ? parseInt(panelState.userId, 10) : null;

  const { data: workorders, isLoading } = useMyWeekWorkorders(
    userId,
    weekStartIso,
  );

  const groups = useMemo(
    () => groupByDay(workorders ?? [], weekStart),
    [workorders, weekStart],
  );

  const handleUserChange = useCallback(
    (newUserId: number | null) => {
      setPanelState({ userId: newUserId?.toString() });
    },
    [setPanelState],
  );

  const today = new Date();

  return (
    <Panel {...panelProps} title="My Workorders">
      <div className="bg-card sticky top-[41px] z-[5] space-y-2 pb-2 md:top-[49px]">
        <WorkorderNavigation date={dateParam} mode={mode} />
        <div className="flex items-center justify-between gap-2">
          <UserSwitcher
            selectedUserId={userId}
            onUserChange={handleUserChange}
          />
          <ViewModeToggle mode={mode} onChange={onModeChange} />
        </div>
      </div>

      {isLoading ? (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-live="polite"
        >
          <Loader2
            className="text-muted-foreground h-8 w-8 animate-spin"
            aria-hidden="true"
          />
          <span className="sr-only">Loading workorders...</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          No workorders for this week
        </div>
      ) : (
        <div className="space-y-6 pb-4">
          {groups.map((group) => (
            <section key={group.key} className="space-y-2">
              <DayGroupHeader group={group} today={today} />
              <div className="space-y-2 sm:space-y-1">
                {group.workorders.map((wo, idx) => (
                  <WorkorderCard
                    key={wo.recnum}
                    mode="readonly"
                    workorder={wo}
                    index={idx}
                    linkBase={path}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
};
